import React from "react";
import { Box, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import { dt } from "../../utils/dt";
import { Machine } from "../../models/models";
import { getMachineDisplayName, getMachineName, getBrandDisplayName, getProductionYearDisplay, getMachineTypeLabel } from "../../../../shared/utils/machineUtils";
import { ImageGallery, useGalleryImages } from "../../../../shared/components/gallery";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrMachineCardProps {
  machine: Machine;
  machineId?: string;
}

const SrMachineCard = ({ machine, machineId }: SrMachineCardProps) => {
  const { translate } = useTranslation("myMachinery");
  const navigate = useNavigate();

  const displayName = getMachineDisplayName(machine);
  const subtitle = getMachineName(machine);
  const brandName = getBrandDisplayName(machine);
  const yearDisplay = getProductionYearDisplay(machine);

  const galleryImages = useGalleryImages({
    type: "machine",
    image: machine?.image?.content || null,
    catalogImageUrl:
      machine?.catalogModel?.imageStandardUrl ||
      machine?.catalogModel?.imageUrl ||
      null,
    catalogImages: machine?.catalogModel?.images || [],
    files:
      machine?.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  return (
    <Box
      onClick={() => machineId && navigate(`/machinery/${machineId}`)}
      sx={{
        background: dt.machinery.card.bg,
        border: dt.machinery.card.border,
        borderRadius: dt.machinery.card.radius,
        boxShadow: dt.machinery.card.shadow,
        padding: "20px",
        fontFamily: dt.font,
        cursor: "pointer",
        transition: "box-shadow 0.2s ease, transform 0.15s ease",
        "&:hover": {
          boxShadow: dt.machinery.card.shadowHover,
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Machine image */}
      <Box sx={{ mx: "auto", mb: "14px" }}>
        <ImageGallery images={galleryImages} variant="card" />
      </Box>

      {/* Machine name */}
      <Box
        sx={{
          fontSize: "16px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: dt.text.primary,
          textAlign: "center",
          lineHeight: 1.3,
          wordBreak: "break-word",
        }}
      >
        {displayName}
      </Box>

      {/* Subtitle */}
      {subtitle && (
        <Box
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            fontFamily: dt.font,
            color: dt.text.secondary,
            textAlign: "center",
            mt: "4px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {subtitle}
        </Box>
      )}

      {/* Spec details */}
      <Box
        sx={{
          mt: "14px",
          pt: "14px",
          borderTop: `1px dashed ${dt.border.light}`,
          display: "flex",
          flexDirection: "column",
          gap: "7px",
        }}
      >
        {machine.serialNumber && (
          <CompactSpecLine
            label={translate("sn")}
            value={machine.serialNumber}
            mono
          />
        )}
        {brandName && (
          <CompactSpecLine label={translate("brand")} value={brandName} />
        )}
        {machine.type?.name && (
          <CompactSpecLine
            label={translate("type")}
            value={getMachineTypeLabel(machine.type)}
          />
        )}
        {yearDisplay && (
          <CompactSpecLine
            label={translate("productionYear")}
            value={yearDisplay}
            mono
          />
        )}

        {/* Location row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 400,
              fontFamily: dt.font,
              color: dt.text.tertiary,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {translate("locationLabel")}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              minWidth: 0,
            }}
          >
            <PlaceOutlined
              sx={{
                fontSize: "13px",
                color: machine.locationName ? dt.blue : dt.text.tertiary,
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: machine.locationName ? 500 : 400,
                fontFamily: dt.font,
                color: machine.locationName ? dt.text.primary : dt.text.tertiary,
                fontStyle: machine.locationName ? "normal" : "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                textAlign: "right",
              }}
            >
              {machine.locationName
                ? machine.parentLocationName
                  ? `${machine.parentLocationName} > ${machine.locationName}`
                  : machine.locationName
                : translate("locationNotSet")}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* View profile hint */}
      <Box
        sx={{
          mt: "12px",
          fontSize: "12px",
          fontWeight: 600,
          fontFamily: dt.font,
          color: dt.blue,
          textAlign: "center",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {translate("srDetail.viewMachineProfile")}
      </Box>
    </Box>
  );
};

// ===================================================================
//  COMPACT SPEC LINE
// ===================================================================
const CompactSpecLine = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: "8px",
    }}
  >
    <Box
      sx={{
        fontSize: "11px",
        fontWeight: 400,
        fontFamily: dt.font,
        color: dt.text.tertiary,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        fontFamily: mono ? dt.fontMono : dt.font,
        color: dt.text.primary,
        letterSpacing: mono ? "0.3px" : undefined,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        textAlign: "right",
      }}
    >
      {value}
    </Box>
  </Box>
);

export default SrMachineCard;
